<template>
  <div class="fts-calendar-view">
    <template v-if="isInitialLoaded">
      <fts-listing-actions
        :module-id="moduleId"
        :listing-id="listingId"
        :standard-activities="headerStandards"
        :activities="headerActivities"
        :documents="headerDocuments"
        :customs="headerCustoms"
      />

      <fts-listing-tools
        :workspace-id="workspaceId"
        :workspace-beta="workspaceBeta"
        :workspace-context="workspaceContext"
        :modules="modules"
        :states="states"
        :users="users"
        :module-id="moduleId"
        :module-import="moduleImport"
        :listing-id="listingId"
        :columns="columns"
        :filter-columns="filterColumns"
        :filters="filters"
        :skip-filters-watch="skipFiltersWatch"
        :sorts.sync="sorts"
        :standard-activities="headerStandards"
        :loading="loading"
        :excludes="['bulk']"
        :total-items-exceed="isTotalItemsExceed"
        @refresh="fetchData"
        @filters:changed="updateFilters"
        @sorts:changed="fetchData"
        ref="ftsListingTools"
      />
    </template>

    <fts-full-calendar 
      :events="events" 
      @active-date:changed="handleActiveDateChanged" 
      v-show="isInitialLoaded"
    >
      <div slot="headerContent" class="date-selection">
        <div class="date-label">start from</div>
        <fts-select
          :options="getEventStartFieldOptions"
          label="displayName"
          :reduce="(x) => x"
          :clearable="false"
          placeholder="start date"
          :form-data="formData"
          :item="{ fieldName: 'eventStartField' }"
          class="date-select"
          @input="handleSelectChanged"
        />
        <div class="date-label">until</div>
        <fts-select
          :options="getEventEndFieldOptions"
          label="displayName"
          :reduce="(x) => x"
          placeholder="end date"
          :form-data="formData"
          :item="{ fieldName: 'eventEndField' }"
          class="date-select"
          @input="handleSelectChanged"
        />
      </div>
      <!-- message -->
      <div slot="content" class="cv-message" v-if="isTotalItemsExceed">    
        <i class="fts-icon-exclamation-triangle" />
        Unable to display all entries on the calendar as the total number exceeds the limit.
      </div>
      <!-- events -->
      <template #eventContent="{ event, contentClass, titleClass }">
        <fts-tippy
          tag="a"
          :href="locusUrl(event.id)"
          :content="event.title"
          :trigger-class="titleClass"
          :class="contentClass"
          class="cv-event fts-cursor-pointer"
          @click.native.prevent="executeActivity(event.id)"
          v-if="isViewRight"
        >
          {{event.title}}
        </fts-tippy>
        <fts-tippy
          :content="event.title"
          :trigger-class="titleClass"
          :class="contentClass"
          class="cv-event fts-cursor-default"
          v-else
        >
          {{event.title}}
        </fts-tippy>
      </template>
    </fts-full-calendar>

    <fts-feedback-indicate ref="ftsFeedbackIndicate" />

    <fts-spinner full-screen boundary="parent" v-if="loading" />
  </div>
</template>

<script>
import changesets from "diff-json";

import commons from '../../commons/commons';
import { isCapableOf, Listing_Max_Total_Entries, SYSTEM_CAPABILITY } from '../../commons/flags';
import queryLogic from '../../commons/queryLogic';
import listing from '../../commons/listing';
import { clientStore } from '../../store/clientStore';
import LocalStorageService from '../../store/LocalStorageService';

import FTSFeedbackIndicate from '../FTSFeedbackIndicate';
import FTSFullCalendar from '../FTSFullCalendar';
import FTSListingActions from '../listing/FTSListingActions';
import FTSListingTools from '../listing/FTSListingTools';
import FTSSelect from '../FTSSelect';
import FTSSpinner from '../FTSSpinner';
import FTSTippy from '../FTSTippy';

export default {
  components: {
    "fts-feedback-indicate": FTSFeedbackIndicate,
    "fts-full-calendar": FTSFullCalendar,
    "fts-listing-actions": FTSListingActions,
    "fts-listing-tools": FTSListingTools,
    "fts-select": FTSSelect,
    "fts-spinner": FTSSpinner,
    "fts-tippy": FTSTippy
  },
  props: {
    workspaceId: [String, Number],
    workspaceBeta: Boolean,
    workspaceContext: Object,
    modules: Array,
    states: Array,
    users: Array,
    moduleId: [String, Number],
    listingId: String
  },
  data: () => ({
    SYSTEM_CAPABILITY: Object.freeze(SYSTEM_CAPABILITY),
    header: {},
    columns: [],
    filters: null,
    originalDefaultFilters: null,
    skipFiltersWatch: false,
    sorts: null,
    resultSet: { list: []},
    moduleImport: null,
    formData: {},
    isInitialLoaded: false,
    isSchemaLoaded: false,
    loading: false
  }),
  methods: {
    isCapableOf(type) {
      return isCapableOf(type, this.modules, this.moduleId);
    },
    isRightOf(type) {
      return this.header && this.header.standards && this.header.standards.indexOf(type) > -1;
    },
    locusUrl(id) {
      return commons.clientPathUrl({
        moduleId: this.moduleId,
        listId: this.listingId,
        id: id,
      });
    },
    composeFilterColumns(columns) {
      let filterColumns = _.cloneDeep(columns);
      // collaboration
      filterColumns.splice(1, 0, {
        ...queryLogic.querySelectionPrefix.find(x => x.id === "collaboration")
      });
      // assign to
      const assignToIndex = filterColumns.findIndex(x => x.id === "assignTo");
      if (assignToIndex > -1) {
        const isDueExisted = filterColumns.some(x => x.id === "due");
        if (!isDueExisted) {
          filterColumns.splice(assignToIndex + 1, 0, {
            ...queryLogic.querySelectionPrefix.find(x => x.id === "due")
          });
        }
      }
      // created date
      const createdByIndex = filterColumns.findIndex(x => x.id === "createdBy");
      if (createdByIndex > -1) {
        const isCreatedDateExisted = filterColumns.some(x => x.id === "createdDate");
        if (!isCreatedDateExisted) {
          filterColumns.splice(createdByIndex + 1, 0, {
            ...queryLogic.querySelectionPrefix.find(x => x.id === "createdDate")
          });
        }
      }
      // last change date
      const lastChangeByIndex = filterColumns.findIndex(x => x.id === "lastChangeBy");
      if (lastChangeByIndex > -1) {
        const isLastChangeDateExisted = filterColumns.some(x => x.id === "lastChangeDate");
        if (!isLastChangeDateExisted) {
          filterColumns.splice(lastChangeByIndex + 1, 0, {
            ...queryLogic.querySelectionPrefix.find(x => x.id === "lastChangeDate")
          });
        }
      }
      
      return filterColumns.filter(column => {
        if (column.system) return true;

        const informationType = commons.informationTypes.find(x => x.id === column.type);
        return Boolean(informationType && informationType.searchable);
      });
    },
    initialEntryView() {
      if (
        this.$route.query.id || 
        (this.$route.query.a === "create" && this.isCreateRight)
      ) {
        let entryId = null;
        let moduleId = this.moduleId;
        let listingId = this.$route.query.l;

        if (this.$route.query.id) {
          const queryDecodeids = commons.queryDecodeids(this.$route.query.id);

          if (queryDecodeids && queryDecodeids.entryId) {
            entryId = queryDecodeids.entryId;
          }

          if (queryDecodeids && queryDecodeids.moduleId) {
            moduleId = queryDecodeids.moduleId;

            if (this.moduleId != moduleId) {
              listingId = null;
            }
          }
        }

        window.eventBus.$emit("quickViewData", {
          id: entryId,
          vectorId: moduleId,
          listingId: listingId,
          activityId: this.$route.query.a,
          fullView: this.$route.query.m && this.$route.query.toLowerCase() === "f"
        });
      }
    },
    executeActivity(entryId, activityId = "quickView") {
      this.$emit("executeActivity", {
        activity: {id: activityId},
        items: [{id: entryId}]
      });
    },
    setPreference() {
      const eventStartFieldId = this.formData.eventStartField && this.formData.eventStartField.id;
      const eventEndFieldId = this.formData.eventEndField && this.formData.eventEndField.id;
      let preference = {...LocalStorageService.getModuleListingPreference(this.moduleId, this.listingId)};

      if (!!eventStartFieldId) {
        preference.calendarStartDateId = eventStartFieldId;
      } else {
        delete preference.calendarStartDateId;
      }

      if (!!eventEndFieldId) {
        preference.calendarEndDateId = eventEndFieldId;
      } else {
        delete preference.calendarEndDateId;
      }

      LocalStorageService.setModuleListingPreference(this.moduleId, this.listingId, preference);
    },
    async handleActiveDateChanged(payload) {
      this.$set(this.formData, "startDate", payload.startDate);
      this.$set(this.formData, "endDate", moment(payload.endDate).subtract(1, "days").format());

      if (this.isInitialLoaded) {
        this.debouncedApiCall();
      } else {
        this.loading = true;
        this.setInitialFilters();
        await this.refresh({ withHeader: true, withPreferenceFilters: true });
        this.initialEntryView();
        this.loading = false;
      }
    },
    handleSelectChanged() {
      this.setPreference();
      this.debouncedApiCall();
    },
    async loadWorkspaceList(payload = {}) {
      let eventStartFieldName = this.formData.eventStartField && this.formData.eventStartField.fieldName || "lastChangeDate";
      let eventEndFieldName = this.formData.eventEndField && this.formData.eventEndField.fieldName || null;

      if (eventStartFieldName === eventEndFieldName) {
        eventEndFieldName = null;
      }

      return await clientStore.dispatch("loadWorkspaceList", {
        moduleId: this.moduleId,
        listingId: this.listingId,
        withHeader: (payload && payload.withHeader) || false,
        filters: (payload && payload.filters) || null,
        sorts: this.sorts ? this.sorts : null,
        contextId: this.workspaceContext != null ?
          this.workspaceContext.id :
          null
        ,
        startFieldName: eventStartFieldName,
        endFieldName: eventEndFieldName,
        startDate: this.formData.startDate,
        endDate: this.formData.endDate
      });
    },
    async refresh(payload = {}) {
      const withHeader = Boolean(payload && payload.withHeader);
      const withPreferenceFilters = Boolean(payload && payload.withPreferenceFilters);

      try {
        let res = null;

        if (withHeader) {
          let preference = null;
          let isPreferenceFilters = false;

          if (withPreferenceFilters) {
            preference = LocalStorageService.getModuleListingPreference(this.moduleId, this.listingId);
            isPreferenceFilters = Boolean(preference && preference.filters);
          }

          try {
            res = await this.loadWorkspaceList({ withHeader: true, filters: this.filters });
          } catch(err) {
            if (isPreferenceFilters) {
              isPreferenceFilters = false;
              this.setLocalStoragePreferenceFilters(preference, null);

              res = await this.loadWorkspaceList({ withHeader: true });
            } else {
              throw err;
            }
          }

          const resultData = _.cloneDeep(res.data);

          if (isPreferenceFilters) {
            const filterColumns = this.composeFilterColumns(resultData.header.columns);
            let clonedFilters = _.cloneDeep(this.filters);
            queryLogic.updateQueryFilters(filterColumns, clonedFilters);
            const diff = changesets.diff(clonedFilters, this.filters);

            if (diff && diff.length) {
              if (
                !Array.isArray(clonedFilters.items) || 
                !clonedFilters.items.length
              ) {
                clonedFilters = null;
              }

              try {
                res = await this.loadWorkspaceList({ filters: clonedFilters });
              } catch(err) {
                clonedFilters = null;
                res = await this.loadWorkspaceList();
              }

              if (clonedFilters) {
                this.setFiltersInternally(clonedFilters);
                this.setLocalStoragePreferenceFilters(preference, clonedFilters);
              } else {
                this.setFiltersInternally({
                  items: [
                    queryLogic.getDefaultQueryFilter(filterColumns, { workspaceBeta: clientStore.getters.workspaceBeta })
                  ]
                });
                
                isPreferenceFilters = false;
                this.setLocalStoragePreferenceFilters(preference, null);
              }
            }
          }

          this.header = resultData.header;
          this.columns = resultData.header.columns;
          this.moduleImport = resultData.import;

          if (
            this.header.defaultFilters && 
            this.header.defaultFilters.items && 
            this.header.defaultFilters.items.length
          ) {
            const { filters, originalDefaultFilters, defaultFilterChanged } = listing.setDefaultFilters(
              this.header.defaultFilters,
              this.originalDefaultFilters,
              this.filters,
              this.columns
            );

            this.originalDefaultFilters = originalDefaultFilters;

            if (!isPreferenceFilters) {
              this.setFiltersInternally(filters);

              if (this.isSchemaLoaded && defaultFilterChanged) {
                res = await this.loadWorkspaceList({ filters: filters });
              }
            }
          } else if (!this.isSchemaLoaded && !isPreferenceFilters) {
            this.setFiltersInternally({
              items: [
                queryLogic.getDefaultQueryFilter(this.columns, { workspaceBeta: this.workspaceBeta })
              ]
            });
            this.originalDefaultFilters = null;
          }
          // default sort
          if (this.header.defaultSort) {
            this.sorts = _.cloneDeep(this.header.defaultSort);
          }
          // system capability
          if (!this.isCapableOf(SYSTEM_CAPABILITY.Write)) {
            this.header.standards = this.header.standards.filter(x =>
              ["create", "edit", "changeStatus", "duplicate", "assign"].indexOf(x) === -1
            );
          }

          if (!this.isCapableOf(SYSTEM_CAPABILITY.Delete)) {
            this.header.standards = this.header.standards.filter(x => x !== "delete");
          }

          if (!this.isCapableOf(SYSTEM_CAPABILITY.Read)) {
            this.header.standards = this.header.standards.filter(x =>
              ["view", "quickView"].indexOf(x) === -1
            );
          }
          // preference
          preference = {...LocalStorageService.getModuleListingPreference(this.moduleId, this.listingId)};
          if (Array.isArray(this.columns)) {
            [
              { key: "calendarStartDateId", fieldName: "eventStartField" },
              { key: "calendarEndDateId", fieldName: "eventEndField" }
            ].forEach(({ key, fieldName }) => {
              if (preference[key]) {
                const res = this.columns.find((x) => x.id == preference[key]);
                if (res) {
                  this.$set(this.formData, fieldName, res);
                }
              }
            });

            if (!(this.formData.eventStartField && this.formData.eventStartField.id)) {
              const res = this.columns.find((x) => x.id == "lastChangeDate");
              if (res) {
                this.$set(this.formData, "eventStartField", res);
              }
            }
          }

          this.isSchemaLoaded = true;
        } else {
          res = await this.loadWorkspaceList({ filters: this.filters });
        }

        this.$refs.ftsFeedbackIndicate && this.$refs.ftsFeedbackIndicate.close();
        this.resultSet = res.data.data;

        if (this.isInitialLoaded !== true) {
          this.isInitialLoaded = true;
        }
      } catch(err) {
        console.error(err);

        if (this.isInitialLoaded) {
          this.$toasted.global.error(err);
        } else {
          this.$refs.ftsFeedbackIndicate && this.$refs.ftsFeedbackIndicate.show({
            type: "error",
            title: "Something went wrong",
            response: err.response,
            disabledAction: true
          });
        }
      }
    },
    async fetchData() {
      this.loading = true;
      await this.refresh();
      this.loading = false;
    },
    async updateFilters() {
      // preference
      const preference = {
        ...LocalStorageService.getModuleListingPreference(this.moduleId, this.listingId)
      }
      this.setLocalStoragePreferenceFilters(preference, this.filters);
      // end preference
      await this.fetchData();
    },
    setFiltersInternally(filters) {
      this.$refs.ftsListingTools && this.$refs.ftsListingTools.cancel();
      this.skipFiltersWatch = true;
      this.filters = filters;
      this.$nextTick(() => {
        this.skipFiltersWatch = false;
      });
    },
    setLocalStoragePreferenceFilters(preference, filters) {
      let savedPreference = { ...preference };

      if (
        filters && 
        Array.isArray(filters.items) && 
        filters.items.length > 0
      ) {
        savedPreference.filters = filters;
      } else {
        delete savedPreference.filters;
      }

      LocalStorageService.setModuleListingPreference(
        this.moduleId,
        this.listingId,
        savedPreference
      );
    },
    setLocalStoragePreferenceFilters(preference, filters) {
      let savedPreference = { ...preference };

      if (
        filters && 
        Array.isArray(filters.items) && 
        filters.items.length > 0
      ) {
        savedPreference.filters = filters;
      } else {
        delete savedPreference.filters;
      }

      LocalStorageService.setModuleListingPreference(
        this.moduleId,
        this.listingId,
        savedPreference
      );
    },
    setInitialFilters() {
      let preference = LocalStorageService.getModuleListingPreference(this.moduleId, this.listingId);

      if (preference && preference.filters) {
        if (
          Array.isArray(preference.filters.items) && 
          preference.filters.items.length
        ) {
          this.setFiltersInternally(preference.filters);
        } else {
          this.setLocalStoragePreferenceFilters(preference, null);
        }
      }
    }
  },
  computed: {
    isViewRight() {
      return this.isRightOf("view") || this.isRightOf("quickView");
    },
    isCreateRight() {
      return this.isRightOf("create");
    },
    events() {
      const list = this.resultSet && this.resultSet.list || [];
      const getValue = (eventField, value) => {
        if (eventField.system) {
          if (eventField.fieldName === "lastChangeDate") {
            return value.audit.updatedDate;
          }

          return value.audit[eventField.fieldName];
        } else {
          return value.extension[eventField.fieldName];
        }
      }

      return list.map(x => {
        let event = {
          id: x.id,
          title: x.title || "(untitled)",
          start: x.audit.updatedDate
        }

        if (this.formData.eventStartField) {
          event.start = getValue(this.formData.eventStartField, x);
        }

        if (this.formData.eventEndField) {
          event.end = getValue(this.formData.eventEndField, x);
        }

        return event;
      });
    },
    getTotalItemsLimit() {
      const limit = this.resultSet && this.resultSet.limit;
      
      if (limit && commons.isInteger(limit) && limit > 0) {
        return limit;
      }

      return Listing_Max_Total_Entries;
    },
    isTotalItemsExceed() {
      if (this.resultSet && this.resultSet.totalItems) {
        return this.resultSet.totalItems >= this.getTotalItemsLimit;
      }

      return false;
    },
    getDateTimeColumns() {
      let dateTimeColumns = this.columns.filter(x => x.type === 5 || x.type === 6);

      if (!dateTimeColumns.some(x => x.id === "createdDate" && x.system === true)) {
        dateTimeColumns.push({
          id: "createdDate",
          displayName: "Created Date",
          system: true,
          fieldName: "createdDate",
          type: 5
        });
      }

      if (!dateTimeColumns.some(x => x.id === "lastChangeDate" && x.system === true)) {
        dateTimeColumns.push({
          id: 'lastChangeDate',
          displayName: "Last Change Date",
          system: true,
          fieldName: "lastChangeDate",
          type: 5
        });
      }

      return dateTimeColumns;
    },
    getEventStartFieldOptions() {
      if (this.formData && this.formData.eventEndField && this.formData.eventEndField.id) {
        return this.getDateTimeColumns.filter(x => x.id !== this.formData.eventEndField.id);
      }

      return this.getDateTimeColumns;
    },
    getEventEndFieldOptions() {
      if (this.formData && this.formData.eventStartField && this.formData.eventStartField.id) {
        return this.getDateTimeColumns.filter(x => x.id !== this.formData.eventStartField.id);
      }

      return this.getDateTimeColumns;
    },
    headerStandards() {
      return this.header && this.header.standards;
    },
    headerActivities() {
      return this.header && this.header.activities;
    },
    headerDocuments() {
      return this.header && this.header.documents;
    },
    headerCustoms() {
      return this.header && this.header.customs;
    },
    filterColumns() {
      let filterColumns = this.composeFilterColumns(this.columns);

      // query selection suffix
      const querySelectionSuffix = queryLogic.querySelectionSuffix
        .filter(x => x.fieldName === "and" || x.fieldName === "or")
        .map(y => (({ ...y, system: true })));

      filterColumns = filterColumns.concat(querySelectionSuffix);

      return filterColumns;
    }
  },
  created() {
    this.debouncedApiCall = _.debounce(() => {
      this.fetchData();
    }, 1000);

    this.refreshSchemaHandler = () => {
      this.refresh({ withHeader: true, withPreferenceFilters: true });
    }

    this.refreshListHandler = (payload = {}) => {
      if (!payload.moduleId || payload.moduleId == this.moduleId) {
        this.fetchData();
      }
    }

    window.eventBus.$on("listing:schemaUpdated", this.refreshSchemaHandler);
    window.eventBus.$on("listing:refresh", this.refreshListHandler);
    window.eventBus.$on("entry:updated", this.refreshListHandler);
  },
  beforeDestroy() {
    window.eventBus.$off("listing:schemaUpdated", this.refreshSchemaHandler);
    window.eventBus.$off("listing:refresh", this.refreshListHandler);
    window.eventBus.$off("entry:updated", this.refreshListHandler);

    if (this.debouncedApiCall && this.debouncedApiCall.cancel) {
      this.debouncedApiCall.cancel();
    }
  }
};
</script>

<style lang="scss">
@use "./src/assets/scss/vars.scss" as *;

.fts-calendar-view {
  .date-selection {
    display: flex;
    align-items: center;
    margin-right: 1rem;
    .date-label {
      margin-right: 0.5rem;
      font-size: 0.875rem;
      line-height: 1.5;
      white-space: nowrap;
      color: var(--fts-text);
      &:not(:first-child) {
        margin-left: 0.5rem;
      }
    }
    .date-select {
      width: 200px;
    }
  }
  .cv-event {
    text-decoration: none;
    color: #fff;
  }
  .cv-message {
    margin-bottom: 0.75rem;
    padding: 0.375rem 0.75rem;
    border-radius: 0.25rem;
    background-color: var(--fts-gray-100);
    color: var(--fts-text);
    font-size: 0.875rem;
    line-height: 1.5;
    i {
      padding-right: 0.125rem;
      vertical-align: middle;
    }
  }
}
</style>